import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoginBackdrop from './LoginBackdrop';
import OperatorMfaStep from './OperatorMfaStep';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import authService from '../services/authService';
import { getMarketingUrl, isAppMode } from '../utils/deploymentMode';

// Captcha widget component — renders the appropriate provider
const CaptchaWidget = ({ provider, siteKey, onVerify, onExpire }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const renderAttempted = useRef(false);

  const renderCaptcha = useCallback(() => {
    if (!containerRef.current || renderAttempted.current) return;

    if (provider === 'RECAPTCHA_V2') {
      if (window.grecaptcha && window.grecaptcha.render) {
        renderAttempted.current = true;
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: onVerify,
          'expired-callback': onExpire,
        });
      }
    } else if (provider === 'RECAPTCHA_V3') {
      // V3 is invisible — execute on form submit instead
      renderAttempted.current = true;
    } else if (provider === 'HCAPTCHA') {
      if (window.hcaptcha && window.hcaptcha.render) {
        renderAttempted.current = true;
        widgetIdRef.current = window.hcaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: onVerify,
          'expired-callback': onExpire,
        });
      }
    } else if (provider === 'TURNSTILE') {
      if (window.turnstile && window.turnstile.render) {
        renderAttempted.current = true;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: onVerify,
          'expired-callback': onExpire,
        });
      }
    }
  }, [provider, siteKey, onVerify, onExpire]);

  useEffect(() => {
    renderAttempted.current = false;

    // Hide the floating reCAPTCHA badge — Google allows this if text attribution is included
    const badgeStyle = document.createElement('style');
    badgeStyle.id = 'captcha-badge-hide';
    badgeStyle.textContent = '.grecaptcha-badge { visibility: hidden !important; }';
    if (!document.getElementById('captcha-badge-hide')) {
      document.head.appendChild(badgeStyle);
    }

    // Determine which script to load
    let scriptSrc = null;
    let scriptId = 'captcha-script';
    if (provider === 'RECAPTCHA_V2') {
      scriptSrc = 'https://www.google.com/recaptcha/api.js?render=explicit';
    } else if (provider === 'RECAPTCHA_V3') {
      scriptSrc = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    } else if (provider === 'HCAPTCHA') {
      scriptSrc = 'https://js.hcaptcha.com/1/api.js?render=explicit';
    } else if (provider === 'TURNSTILE') {
      scriptSrc = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    }

    if (!scriptSrc) return;

    const existing = document.getElementById(scriptId);
    if (existing) {
      // Script already loaded — try rendering immediately
      renderCaptcha();
      // Also poll in case API isn't ready yet
      const interval = setInterval(() => {
        renderCaptcha();
        if (renderAttempted.current) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Wait for API to be ready
      const interval = setInterval(() => {
        renderCaptcha();
        if (renderAttempted.current) clearInterval(interval);
      }, 200);
      setTimeout(() => clearInterval(interval), 10000);
    };
    document.head.appendChild(script);

    // Cleanup: remove script, badge, and styles when component unmounts (after login)
    return () => {
      const scriptEl = document.getElementById(scriptId);
      if (scriptEl) scriptEl.remove();
      const hideStyle = document.getElementById('captcha-badge-hide');
      if (hideStyle) hideStyle.remove();
      // Remove reCAPTCHA badge injected by Google
      document.querySelectorAll('.grecaptcha-badge').forEach(b => b.remove());
      // Remove any leftover reCAPTCHA iframes
      document.querySelectorAll('iframe[src*="recaptcha"]').forEach(f => f.remove());
      // Clean up global grecaptcha object
      if (window.grecaptcha) {
        try { delete window.grecaptcha; } catch (_) { window.grecaptcha = undefined; }
      }
    };
  }, [provider, siteKey, renderCaptcha]);

  // V3 is invisible — no container needed
  if (provider === 'RECAPTCHA_V3') return null;

  return <Box ref={containerRef} sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }} />;
};

const LoginPage = () => {
  const [orgSlug, setOrgSlug] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [mfa, setMfa] = useState(null); // { mode: 'enroll'|'verify', pendingToken, username }
  const { login } = useAuth();
  const notification = useNotification();
  const navigate = useNavigate();

  // Clear any invalid tokens when login page is shown
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  }, []);

  // Fetch captcha configuration on mount
  useEffect(() => {
    authService.getCaptchaConfig().then(config => {
      setCaptchaConfig(config);
    }).catch(() => {
      // Captcha config unavailable — proceed without
    });
  }, []);

  const handleCaptchaVerify = useCallback((token) => {
    setCaptchaToken(token);
  }, []);

  const handleCaptchaExpire = useCallback(() => {
    setCaptchaToken(null);
  }, []);

  const navigateAfter = (response) => {
    if (response?.role === 'SUPER_ADMIN') {
      navigate('/choose-context');
    } else if (response?.role === 'WEB_ADMIN') {
      // Website Operator — no platform surface; go to Website Controls.
      navigate('/operator/waitlist');
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!orgSlug.trim()) newErrors.org = 'Organization ID is required';
    if (!username.trim()) newErrors.username = 'Username or email is required';
    if (!password) newErrors.password = 'Password is required';
    if (captchaConfig.enabled && captchaConfig.provider !== 'RECAPTCHA_V3' && !captchaToken) {
      newErrors.captcha = 'Please complete the captcha';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      let tokenToSend = captchaToken;

      // For reCAPTCHA V3, execute at submit time (invisible)
      if (captchaConfig.enabled && captchaConfig.provider === 'RECAPTCHA_V3' && window.grecaptcha) {
        tokenToSend = await new Promise((resolve) => {
          window.grecaptcha.ready(() => {
            window.grecaptcha.execute(captchaConfig.siteKey, { action: 'login' }).then(resolve);
          });
        });
      }

      const response = await login(username, password, orgSlug.trim(), tokenToSend);
      // Operators get a 2FA challenge (no session yet) — switch to the MFA step.
      if (response?.mfaEnrollRequired || response?.mfaRequired) {
        setMfa({
          mode: response.mfaEnrollRequired ? 'enroll' : 'verify',
          pendingToken: response.pendingToken,
          username: response.username,
        });
        setLoading(false);
        return;
      }
      notification.success('Login successful!');
      navigateAfter(response);
    } catch (error) {
      const msg = error?.message || 'Invalid credentials';
      if (msg.toLowerCase().includes('captcha')) {
        setErrors({ captcha: msg });
      } else {
        setErrors({ login: 'Invalid organization, username, or password' });
      }
      notification.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0a0d12',
        overflow: 'hidden',
        padding: 3,
      }}
    >
      <LoginBackdrop />
      <Card
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 420,
          borderRadius: 3,
          border: '1px solid rgba(240,246,252,0.08)',
          boxShadow: '0 30px 90px rgba(0, 0, 0, 0.6)',
        }}
      >
        <CardContent sx={{ p: 5 }}>
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={`${process.env.PUBLIC_URL}/assets/realops.png`}
              alt="Logo"
              style={{ width: "170px", height: "auto", marginBottom: '16px' }}
            />
            <Typography variant="body2" color="text.secondary">
              Sign in to your account
            </Typography>
          </Box>

          {mfa ? (
            <OperatorMfaStep
              mode={mfa.mode}
              pendingToken={mfa.pendingToken}
              username={mfa.username}
              onSuccess={(resp) => { notification.success('Login successful!'); navigateAfter(resp); }}
              onCancel={() => { setMfa(null); setErrors({}); }}
            />
          ) : (
          <form onSubmit={handleLoginSubmit}>
            <TextField
              fullWidth
              label="Organization ID"
              value={orgSlug}
              onChange={(e) => setOrgSlug(e.target.value)}
              error={Boolean(errors.org)}
              helperText={errors.org}
              margin="normal"
              variant="outlined"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Username or email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              error={Boolean(errors.username)}
              helperText={errors.username}
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
              autoComplete="username"
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={Boolean(errors.password)}
              helperText={errors.password}
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoComplete="current-password"
            />

            {/* Captcha Widget */}
            {captchaConfig.enabled && (
              <CaptchaWidget
                provider={captchaConfig.provider}
                siteKey={captchaConfig.siteKey}
                onVerify={handleCaptchaVerify}
                onExpire={handleCaptchaExpire}
              />
            )}

            {errors.captcha && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.captcha}
              </Typography>
            )}

            {errors.login && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.login}
              </Typography>
            )}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                height: 48,
                fontSize: '1rem',
                fontWeight: 500,
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          )}

          {/* Footer */}
          {captchaConfig.enabled && captchaConfig.provider?.startsWith('RECAPTCHA') && (
            <Typography
              variant="caption"
              color="text.disabled"
              align="center"
              sx={{ mt: 2, display: 'block', fontSize: '0.65rem' }}
            >
              Protected by reCAPTCHA.{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>Privacy</a>
              {' & '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>Terms</a>
            </Typography>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: captchaConfig.enabled ? 1 : 3 }}
          >
            By Attuned Technology
          </Typography>
          {/* Cross-host "back to website" link. In dual mode (single-host
              dev) this stays as {@code /} on the same origin; in production
              with split hosts ({@code app.verops.com} ↔ {@code verops.com})
              it navigates back to the marketing site. Only render in app
              mode — dual mode users are already on the website. */}
          {isAppMode() && (
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ display: 'block', mt: 1 }}
            >
              <a
                href={getMarketingUrl('/')}
                style={{ color: 'inherit', textDecoration: 'none' }}
                onMouseOver={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                ← Back to website
              </a>
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
